import { createStyles, makeStyles } from "@material-ui/core";
import {
    AreaNotification,
    Button,
    EditIcon,
    EnhancedTable,
    NoResultsText,
    PageActionButton,
    PlusIcon,
    RelodeTheme,
    TableColumn,
    TableOptions,
    TrashIcon,
} from "@relode/core";
import { ClientUserRole, JobStatus } from "@relode/core/domain";
import { format } from "date-fns";
import * as React from "react";

import { Team, TeamMember } from "../../../../../shared/types";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                margin: theme.spacing(3, 3),
            },
            editIcon: {
                marginRight: theme.spacing(1),
            },
            button: {
                minWidth: 180,
                padding: 0,
            },
            actions: {
                display: "flex",
            },
        }),
    { name: "TeamMembersDesktop" }
);

interface TeamMembersDesktopProps {
    team: Team;
    handleOpenTeamMembersModal: () => void;
    handleEditTeamMember: (teamMember: TeamMember) => void;
    handleDeleteTeamMember: (teamMember: TeamMember) => void;
    jobStatus: JobStatus;
}
export const TeamMembersDesktop = (props: TeamMembersDesktopProps) => {
    const classes = useStyles({});
    const {
        team,
        handleOpenTeamMembersModal,
        handleDeleteTeamMember,
        handleEditTeamMember,
        jobStatus,
    } = props;

    const { teamMembers, canManageTeam } = team;

    const options: TableOptions = {
        hideCheckbox: true,
    };

    const getColumns = (): TableColumn[] => [
        {
            title: "name",
            field: "fullName",
            sortable: false,
            columnSize: "large",
        },
        {
            title: "email",
            field: "email",
            sortable: false,
            columnSize: "medium",
        },
        {
            title: "organization",
            field: "teamName",
            sortable: false,
            columnSize: "medium",
        },
        {
            title: "role",
            field: "jobUserTypeId",
            sortable: false,
            columnSize: "medium",
            render: (teamMember: TeamMember) =>
                ClientUserRole.fromId(teamMember.jobUserTypeId).displayName,
        },
        {
            title: "date added",
            field: "createTimestamp",
            sortable: false,
            columnSize: "medium",
            render: (teamMember: TeamMember) =>
                format(teamMember.createTimestamp, "P"),
        },
        {
            title: "candidates awaiting feedback",
            field: "candidatesAwaitingFeedbackCount",
            sortable: false,
            columnSize: "medium",
        },
        {
            title: "actions",
            field: "actions",
            align: "right",
            render: (teamMember: TeamMember) => (
                <>
                    {!teamMember.isSelf && canManageTeam ? (
                        <div className={classes.actions}>
                            <PageActionButton
                                onClick={() => handleEditTeamMember(teamMember)}
                                color="primary"
                                title="edit team member"
                                data-cy="editTeamMember"
                            >
                                <EditIcon color="primary" />
                            </PageActionButton>
                            <PageActionButton
                                onClick={() =>
                                    handleDeleteTeamMember(teamMember)
                                }
                                color="primary"
                                title="delete team member"
                                data-cy="deleteTeamMember"
                            >
                                <TrashIcon color="primary" />
                            </PageActionButton>
                        </div>
                    ) : null}
                </>
            ),
        },
    ];

    return (
        <div className={classes.root}>
            <AreaNotification areaName="teamPlanWarning" />
            {jobStatus.isPendingOrRequested() ? (
                <NoResultsText data-cy="noResultsText">
                    Not available for jobs in Pending or Requested status.
                </NoResultsText>
            ) : (
                <>
                    {canManageTeam ? (
                        <Button
                            data-cy="addTeamMemberButton"
                            id="addTeamMemberButton"
                            size="small"
                            color="default"
                            variant="contained"
                            onClick={handleOpenTeamMembersModal}
                            title="add members"
                            className={classes.button}
                            startIcon={<PlusIcon />}
                        >
                            <span>team member</span>
                        </Button>
                    ) : null}

                    {teamMembers && teamMembers.length > 0 ? (
                        <EnhancedTable
                            options={options}
                            columns={getColumns()}
                            data={teamMembers}
                            dataCy={"teamMembersDesktop"}
                        />
                    ) : (
                        <NoResultsText>
                            No users assigned to this job.
                        </NoResultsText>
                    )}
                </>
            )}
        </div>
    );
};
